import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "cardBreakpoint", "sticky", "rowFocus"],
    _excluded2 = ["children"],
    _excluded3 = ["children"],
    _excluded4 = ["children"];

function _createForOfIteratorHelper(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (!it) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = it.call(o); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { border, boxShadow, color, mediaGTE, radius, textStyle } from '@repay/cactus-theme';
import PropTypes from 'prop-types';
import React, { createContext, useContext, useLayoutEffect } from 'react';
import styled, { css } from 'styled-components';
import { margin, width } from 'styled-system';
import { extractMargins } from '../helpers/omit';
import { useMergedRefs } from '../helpers/react';
import variant from '../helpers/variant';
import { ScreenSizeContext, SIZES } from '../ScreenSizeProvider/ScreenSizeProvider';
var DEFAULT_CONTEXT = {
  inHeader: false,
  cellIndex: 0,
  variant: 'table',
  dividers: false,
  rowFocus: true
};
var TableContext = /*#__PURE__*/createContext(DEFAULT_CONTEXT);
var Wrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "Wrapper"
})(["max-width:100%;overflow-x:auto;margin:0 16px;", " ", ";"], margin, function (p) {
  return p.fullWidth ? 'width: 100%;' : '';
});
export var Table = /*#__PURE__*/React.forwardRef(function (initProps, ref) {
  var children = initProps.children,
      cardBreakpoint = initProps.cardBreakpoint,
      _initProps$sticky = initProps.sticky,
      sticky = _initProps$sticky === void 0 ? 'none' : _initProps$sticky,
      _initProps$rowFocus = initProps.rowFocus,
      rowFocus = _initProps$rowFocus === void 0 ? true : _initProps$rowFocus,
      props = _objectWithoutProperties(initProps, _excluded);

  var size = useContext(ScreenSizeContext);

  var context = _objectSpread(_objectSpread({}, DEFAULT_CONTEXT), {}, {
    headers: [],
    rowFocus: rowFocus
  });

  var tableRef = React.useRef(null);
  var mergedRef = useMergedRefs(ref, tableRef);
  var marginProps = extractMargins(props);
  useLayoutEffect(function () {
    var heightInfo = [];
    var height = 0;

    if (context.variant === 'card') {
      var _tableRef$current;

      var cells = (_tableRef$current = tableRef.current) === null || _tableRef$current === void 0 ? void 0 : _tableRef$current.querySelectorAll("".concat(StyledCell));

      if (cells && cells.length > 0) {
        cells.forEach(function (cell) {
          var _first$getBoundingCli, _second$getBoundingCl;

          var cellIndex = cell.cellIndex;
          var info = heightInfo[cellIndex] || (heightInfo[cellIndex] = {
            max: 0,
            cells: []
          });

          var _ref = cell.childNodes,
              _ref2 = _slicedToArray(_ref, 2),
              first = _ref2[0],
              second = _ref2[1];

          var rect1 = first === null || first === void 0 ? void 0 : (_first$getBoundingCli = first.getBoundingClientRect) === null || _first$getBoundingCli === void 0 ? void 0 : _first$getBoundingCli.call(first);
          var rect2 = second === null || second === void 0 ? void 0 : (_second$getBoundingCl = second.getBoundingClientRect) === null || _second$getBoundingCl === void 0 ? void 0 : _second$getBoundingCl.call(second);

          if (rect1) {
            if (!rect2 || rect1.width + rect2.width < cell.clientWidth - 16) {
              height = rect1.height;
            } else {
              height = rect1.height + rect2.height + 16;
            }

            info.max = Math.max(info.max, height);
            info.cells.push({
              height: height,
              cell: cell
            });
          }
        }); // We queue the updates then make them all at once to avoid layout thrashing.

        var _iterator = _createForOfIteratorHelper(heightInfo),
            _step;

        try {
          for (_iterator.s(); !(_step = _iterator.n()).done;) {
            var info = _step.value;

            var _iterator2 = _createForOfIteratorHelper(info.cells),
                _step2;

            try {
              for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
                var cellInfo = _step2.value;
                cellInfo.cell.style.minHeight = "".concat(info.max, "px");
              }
            } catch (err) {
              _iterator2.e(err);
            } finally {
              _iterator2.f();
            }
          }
        } catch (err) {
          _iterator.e(err);
        } finally {
          _iterator.f();
        }
      }
    }
  });

  if (props.variant) {
    context.variant = props.variant;
  } else if (cardBreakpoint && size <= SIZES[cardBreakpoint]) {
    context.variant = 'card';
  }

  if (props.dividers) {
    context.dividers = props.dividers;
  }

  props.variant = context.variant;
  return /*#__PURE__*/React.createElement(TableContext.Provider, {
    value: context
  }, props.as ? /*#__PURE__*/React.createElement(StyledTable, Object.assign({}, props, {
    sticky: sticky,
    ref: mergedRef
  }), children) : /*#__PURE__*/React.createElement(Wrapper, Object.assign({}, marginProps, {
    fullWidth: props.fullWidth
  }), /*#__PURE__*/React.createElement(StyledTable, Object.assign({}, props, {
    sticky: sticky,
    ref: mergedRef
  }), children)));
});
export var TableCell = /*#__PURE__*/React.forwardRef(function (_ref3, ref) {
  var children = _ref3.children,
      props = _objectWithoutProperties(_ref3, _excluded2);

  var context = useContext(TableContext);

  if (context.cellType && !props.as) {
    props.as = context.cellType;
  }

  var colSpan = parseInt(props.colSpan || '1');

  if (context.inHeader && context.headers) {
    context.headers[context.cellIndex] = children;
    context.cellIndex += colSpan;
  } else if (context.variant === 'card') {
    var headerContent = context.headers && context.headers[context.cellIndex];
    context.cellIndex += colSpan;
    props.variant = 'card';
    return /*#__PURE__*/React.createElement(StyledCell, Object.assign({}, props, {
      ref: ref
    }), headerContent && /*#__PURE__*/React.createElement(HeaderBox, null, headerContent), /*#__PURE__*/React.createElement(ContentBox, null, children));
  }

  props.variant = context.variant;
  return /*#__PURE__*/React.createElement(StyledCell, Object.assign({}, props, {
    ref: ref
  }), children);
});
export var TableHeader = /*#__PURE__*/React.forwardRef(function (_ref4, ref) {
  var children = _ref4.children,
      props = _objectWithoutProperties(_ref4, _excluded3);

  var context = useContext(TableContext);
  return /*#__PURE__*/React.createElement(TableContext.Provider, {
    value: _objectSpread(_objectSpread({}, context), {}, {
      inHeader: true,
      cellType: 'th',
      cellIndex: 0
    })
  }, /*#__PURE__*/React.createElement(StyledHeader, Object.assign({}, props, {
    variant: context.variant,
    ref: ref,
    dividers: context.dividers
  }), /*#__PURE__*/React.createElement("tr", null, children)));
});
export var TableRow = function TableRow(_ref5) {
  var children = _ref5.children,
      props = _objectWithoutProperties(_ref5, _excluded4);

  var context = useContext(TableContext);
  var tabIndex = context.rowFocus === 'mouse-only' ? -1 : context.rowFocus ? 0 : undefined;
  return /*#__PURE__*/React.createElement(TableContext.Provider, {
    value: _objectSpread(_objectSpread({}, context), {}, {
      cellIndex: 0
    })
  }, /*#__PURE__*/React.createElement("tr", Object.assign({
    tabIndex: tabIndex
  }, props), children));
};
export var TableBody = 'tbody';
var DefaultTable = Table;
DefaultTable.Header = TableHeader;
DefaultTable.Cell = TableCell;
DefaultTable.Row = TableRow;
DefaultTable.Body = TableBody;
TableCell.propTypes = {
  align: PropTypes.oneOf(['center', 'right', 'left']),
  as: PropTypes.oneOf(['th', 'td'])
};
Table.displayName = 'Table';
Table.propTypes = {
  fullWidth: PropTypes.bool,
  // @ts-ignore
  rowFocus: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['mouse-only'])]),
  rowHover: PropTypes.bool,
  cardBreakpoint: PropTypes.oneOf(['tiny', 'small', 'medium', 'large', 'extraLarge']),
  variant: PropTypes.oneOf(['table', 'card', 'mini'])
};
Table.defaultProps = {
  cardBreakpoint: 'tiny',
  rowFocus: true,
  rowHover: true
};
export default DefaultTable;

var getShape = function getShape(location) {
  return css(["border-", "-radius:", ";"], location, radius(8));
};

var HeaderBox = /*#__PURE__*/styled.div.attrs({
  'aria-hidden': 'true'
}).withConfig({
  displayName: "HeaderBox"
})(["text-transform:uppercase;font-weight:600;text-align:left;max-width:100%;flex-grow:1;margin-right:8px;&:empty{display:none;}"]);
var ContentBox = /*#__PURE__*/styled.div.withConfig({
  displayName: "ContentBox"
})(["flex-grow:1;text-align:right;overflow-wrap:break-word;word-wrap:break-word;min-width:20%;&:only-child,", ":empty + &{text-align:center;}", ":not(:empty) + &{align-self:flex-end;}"], HeaderBox, HeaderBox);
var StyledCell = /*#__PURE__*/styled.td.withConfig({
  displayName: "StyledCell"
})(variant({
  table: css(["text-align:", ";padding:16px;", ";"], function (p) {
    return p.align || 'left';
  }, function (p) {
    return p.width ? width : css(["min-width:calc(160px * 0.7125);", "{min-width:calc(160px * 0.875);}", "{min-width:160px;}"], mediaGTE('large'), mediaGTE('extraLarge'));
  }),
  mini: css(["text-align:", ";padding:8px;", ";"], function (p) {
    return p.align || 'left';
  }, function (p) {
    return p.width && width;
  }),
  card: css(["&&{display:flex;width:240px;flex-flow:row wrap;justify-content:space-between;align-items:flex-start;box-sizing:border-box;padding:8px 16px;:nth-of-type(even){background-color:", ";}:last-child{border-bottom-left-radius:inherit;border-bottom-right-radius:inherit;}}"], color('lightContrast'))
}));
var headerVariants = variant({
  card: css(["tr{position:absolute;top:auto;left:-10000px;}"])
});
var StyledHeader = /*#__PURE__*/styled.thead.withConfig({
  displayName: "StyledHeader"
})(["&&&&& th,&&&&& td{text-transform:uppercase;border:", ";border-right:", ";", ";}", ""], border('base'), function (p) {
  return p.dividers ? border(p.theme, 'mediumContrast') : '';
}, function (p) {
  return p.theme.colorStyles.base;
}, headerVariants);

var getCTABorder = function getCTABorder(p, focus) {
  return css(["th,td{border-left-color:", ";border-top-color:", ";border-bottom-color:", ";background-color:", ";}td:last-child{border-color:", ";}"], p.theme.colors.callToAction, p.theme.colors.callToAction, p.theme.colors.callToAction, focus && p.theme.colors.lightCallToAction, p.theme.colors.callToAction);
};

var table = /*#__PURE__*/css(["display:table;", ";border-spacing:0;td,th{border-right:", ";background-color:", ";border-top:", ";border-bottom:", ";:first-child{border-left:", ";}:last-child{border-right:", ";:focus-within{z-index:1;}", ";}}tr:nth-of-type(even){td,th{background-color:", ";}td:not(:last-child){border-right:", ";}}&&& tr:hover{", "}&&& tr:focus{outline:0;", "}& > tr:first-of-type,tbody > tr:first-child,thead > tr:first-child{th,td{border-top-color:", ";:first-child{", ";}:last-child{", ";}}}&& > thead + tr:first-of-type,&& > thead + tbody > tr:first-child{th,td{border-top-color:transparent;border-top-left-radius:0;border-top-right-radius:0;}}&,tbody:last-child,thead:last-child,tfoot:last-child{& > tr:last-child{th,td{border-bottom-color:", ";:first-child{", ";}:last-child{", ";}}}}"], function (p) {
  return textStyle(p.theme, p.variant === 'mini' ? 'small' : 'body');
}, function (p) {
  return p.dividers ? border(p.theme, 'lightContrast') : '';
}, color('white'), border('transparent'), border('transparent'), border('lightContrast'), border('lightContrast'), function (p) {
  return p.sticky === 'right' ? "position: sticky; right: 0; ".concat(boxShadow(p.theme, 0)) : '';
}, color('lightContrast'), function (p) {
  return p.dividers ? border(p.theme, 'white') : '';
}, function (p) {
  return p.rowHover && getCTABorder(p);
}, function (p) {
  return getCTABorder(p, true);
}, color('lightContrast'), getShape('top-left'), getShape('top-right'), color('lightContrast'), getShape('bottom-left'), getShape('bottom-right'));
var card = /*#__PURE__*/css(["", ";overflow-wrap:break-word;&,thead,tbody,tfoot{display:inline-flex;flex-flow:row nowrap;justify-content:flex-start;}tr{display:flex;flex-flow:column nowrap;min-width:240px;max-width:360px;", " background-color:", ";border:", ";border-radius:", ";margin:4px;outline:0;:focus{border-color:", ";}}th,td{display:block;text-align:center;max-width:100%;padding:8px 16px;:nth-of-type(even){background-color:", ";}:last-child{border-bottom-left-radius:inherit;border-bottom-right-radius:inherit;}}"], textStyle('tiny'), boxShadow(1), color('white'), border('lightContrast'), radius(8), color('callToAction'), color('lightContrast'));
var StyledTable = /*#__PURE__*/styled.table.withConfig({
  displayName: "StyledTable"
})(["", ";color:", ";th{font-weight:600;}", ";"], function (p) {
  return p.fullWidth ? 'min-width: 100%' : '';
}, color('darkestContrast'), variant({
  table: table,
  card: card,
  mini: table
}));